// Typed bindings over the generated OpenAPI client (./schema.ts).
//
// schema.ts is produced by `make openapi` (openapi-typescript, version pinned)
// and committed — never hand-edit it. These helpers layer end-to-end request /
// response types onto the shared fetch transport in services/api.ts, so the SPA
// and the Go API cannot drift: a contract change regenerates schema.ts and any
// mismatch surfaces as a TypeScript error here.

import { api, apiBlob, type BlobResponse } from '../services/api';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export type HealthStatus = Schemas['HealthStatus'];
export type Me = Schemas['Me'];
export type ContactRequest = Schemas['ContactRequest'];
export type ContactResult = Schemas['ContactResult'];
export type FieldLeadRequest = Schemas['FieldLeadRequest'];
export type LeadResult = Schemas['LeadResult'];
export type LeadSummary = Schemas['LeadSummary'];
export type LeadQueryRequest = Schemas['LeadQueryRequest'];
export type LeadQueryResult = Schemas['LeadQueryResult'];
export type LeadDetail = Schemas['LeadDetail'];
export type LeadDetailResult = Schemas['LeadDetailResult'];
export type ActivityEntry = Schemas['ActivityEntry'];
export type LeadNoteResult = Schemas['LeadNoteResult'];
export type LeadStatusResult = Schemas['LeadStatusResult'];
export type LeadAssignResult = Schemas['LeadAssignResult'];
export type LeadExportRequest = Schemas['LeadExportRequest'];

// Derived from the generated paths map (not the bare schema) so the binding
// breaks if the operation, status code, or media type ever changes.
type HealthzResponse = paths['/healthz']['get']['responses'][200]['content']['application/json'];

export function getHealthz(signal?: AbortSignal): Promise<HealthzResponse> {
  return api<HealthzResponse>('/healthz', { signal });
}

// getConsoleStatus reads the visibility console's System-status lens (RAD §10):
// deploy info, live database health, and 24h traffic metrics. Behind the
// desktop-only console×view gate — a non-manager/admin or a phone session is
// rejected (the console route guard already prevents that client-side). The
// response type is pinned to the generated paths map, so a contract change
// surfaces here as a type error.
export type SystemStatus = Schemas['SystemStatus'];

export function getConsoleStatus(signal?: AbortSignal): Promise<SystemStatus> {
  return api<SystemStatus>('/api/console/status', { signal });
}

// getConsoleSecurity reads the visibility console's Security lens (RAD §10): the
// 24h denial posture (how many requests were blocked and why + the busiest
// sources), the IP addresses currently blocked by guard, and the configured
// rate-limit tiers. Behind the desktop-only console×view gate — the console
// route guard already prevents a non-manager/admin or phone session from
// reaching it. The response type is pinned to the generated schema.
export type SecurityPosture = Schemas['SecurityPosture'];
export type BlockedIp = Schemas['BlockedIp'];
export type RateTier = Schemas['RateTier'];

export function getConsoleSecurity(signal?: AbortSignal): Promise<SecurityPosture> {
  return api<SecurityPosture>('/api/console/security', { signal });
}

// postConsoleSecurityUnblock lifts the block on a single IP — the console's one
// mutation (RAD §10/§12), admin-only + desktop-only server-side (a manager who
// can view the lens is still refused). `reason` is required and audited. Returns
// the remaining active blocks so the lens updates in place. The request/response
// types are pinned to the generated paths map, so a contract change surfaces
// here as a type error.
type UnblockBody = paths['/api/console/security/unblock']['post']['requestBody']['content']['application/json'];
type UnblockResp = paths['/api/console/security/unblock']['post']['responses'][200]['content']['application/json'];

export function postConsoleSecurityUnblock(body: UnblockBody, signal?: AbortSignal): Promise<UnblockResp> {
  return api<UnblockResp>('/api/console/security/unblock', { method: 'POST', body, signal });
}

// postConsoleActivity reads a filtered, paginated page of the visibility
// console's audit trail (RAD §10) — who did what, newest first. Behind the
// desktop-only console×view gate (the console route guard already prevents a
// non-manager/admin or phone session from reaching it). POST-for-complex-read
// (WAF Rule 1): the outcome/userId filters and the opaque base64url cursor ride
// the JSON body, never the URL. An empty body asks for the first, unfiltered
// page. Types are pinned to the generated paths map, so a contract change
// surfaces here as a type error.
export type AuditEntry = Schemas['AuditEntry'];
export type ConsoleActivityResult = Schemas['ConsoleActivityResult'];
type ActivityBody = NonNullable<
  paths['/api/console/activity']['post']['requestBody']
>['content']['application/json'];
type ActivityResp = paths['/api/console/activity']['post']['responses'][200]['content']['application/json'];

export function postConsoleActivity(body: ActivityBody = {}, signal?: AbortSignal): Promise<ActivityResp> {
  return api<ActivityResp>('/api/console/activity', { method: 'POST', body, signal });
}

// postConsoleSignIns reads a paginated page of the Activity lens's sign-in feed
// (RAD §10) — session issue / refresh / revoke events, newest first. Same gate +
// WAF posture as postConsoleActivity; pagination only, no filter. Types are
// pinned to the generated paths map.
export type SignInEvent = Schemas['SignInEvent'];
export type ConsoleSignInsResult = Schemas['ConsoleSignInsResult'];
type SignInsBody = NonNullable<
  paths['/api/console/activity/signins']['post']['requestBody']
>['content']['application/json'];
type SignInsResp = paths['/api/console/activity/signins']['post']['responses'][200]['content']['application/json'];

export function postConsoleSignIns(body: SignInsBody = {}, signal?: AbortSignal): Promise<SignInsResp> {
  return api<SignInsResp>('/api/console/activity/signins', { method: 'POST', body, signal });
}

// getConsoleCompliance reads the visibility console's Compliance lens (RAD §12):
// a summary of the personal-data access events over the last 30 days (per-action
// counts + the most recent events). Behind the desktop-only console×view gate —
// the console route guard already prevents a non-manager/admin or phone session
// from reaching it. The response type is pinned to the generated schema.
export type ComplianceSummary = Schemas['ComplianceSummary'];
export type AccessEventCount = Schemas['AccessEventCount'];

export function getConsoleCompliance(signal?: AbortSignal): Promise<ComplianceSummary> {
  return api<ComplianceSummary>('/api/console/compliance', { signal });
}

// getConsoleConfig reads the visibility console's Configuration lens (RAD §10):
// the effective settings (secrets redacted) grouped by area, plus the collection
// inventory. Behind the desktop-only console×view gate — the console route guard
// already keeps a non-manager/admin or phone session out. The endpoint reads no
// database, so it always renders. The response type is pinned to the generated
// schema.
export type EffectiveConfig = Schemas['EffectiveConfig'];
export type SettingGroup = Schemas['SettingGroup'];
export type Setting = Schemas['Setting'];
export type CollectionInfo = Schemas['CollectionInfo'];

export function getConsoleConfig(signal?: AbortSignal): Promise<EffectiveConfig> {
  return api<EffectiveConfig>('/api/console/config', { signal });
}

// getReportsFunnel reads the admin metrics lead funnel (RAD §11): the whole
// current pipeline as counts by stage, the won/lost totals for conversion, and
// the breakdowns by source / product / rep. Behind the desktop-only reports
// all-scope gate — the /reports route guard already keeps a sales rep or phone
// session out. The response type is pinned to the generated schema.
export type FunnelSummary = Schemas['FunnelSummary'];
export type FunnelSegment = Schemas['Segment'];
export type StageCount = Schemas['StageCount'];

export function getReportsFunnel(signal?: AbortSignal): Promise<FunnelSummary> {
  return api<FunnelSummary>('/api/reports/funnel', { signal });
}

// getReportsTrends reads the admin metrics trends-over-time series (RAD §11,
// #48): new leads per day (inflow) and the won/lost outflow over a trailing
// window, from which the client renders the daily volume and the win rate. Same
// desktop-only reports all-scope gate as the funnel. The response type is pinned
// to the generated schema.
export type TrendsSummary = Schemas['TrendsSummary'];
export type TrendPoint = Schemas['TrendPoint'];

export function getReportsTrends(signal?: AbortSignal): Promise<TrendsSummary> {
  return api<TrendsSummary>('/api/reports/trends', { signal });
}

// getReportsTraffic reads the admin metrics site-traffic series (RAD §11, #49):
// the requests the platform served per day (with the 4xx/5xx error split) and the
// busiest endpoints over a trailing window, computed from the append-only audit
// log. Same desktop-only reports all-scope gate as the funnel and trends. The
// response type is pinned to the generated schema.
export type TrafficSummary = Schemas['TrafficSummary'];
export type TrafficPoint = Schemas['TrafficPoint'];
export type EndpointCount = Schemas['EndpointCount'];

export function getReportsTraffic(signal?: AbortSignal): Promise<TrafficSummary> {
  return api<TrafficSummary>('/api/reports/traffic', { signal });
}

// exportDsar downloads the one-click DSAR CSV export for a data subject (RAD §12,
// #44): everything held about the person (leads + consent + activity) as a
// sectioned text/csv attachment. Admin-only + desktop-only server-side
// (RequireAdminConsole) — the lens only shows the control to an admin, and the
// server enforces it regardless. The email rides in the JSON body (WAF-safe),
// and — like exportLeads — the response is a Blob (+ server-supplied filename)
// via apiBlob so the caller can trigger a browser download. The request body
// type is pinned to the generated paths map.
type DsarExportBody = paths['/api/admin/dsr/export.csv']['post']['requestBody']['content']['application/json'];

export function exportDsar(body: DsarExportBody, signal?: AbortSignal): Promise<BlobResponse> {
  return apiBlob('/api/admin/dsr/export.csv', { method: 'POST', body, accept: 'text/csv', signal });
}

// postContact submits the public marketing contact form. The request/response
// types are pinned to the generated paths map, so a contract change surfaces
// here as a type error.
type ContactBody = paths['/api/contact']['post']['requestBody']['content']['application/json'];
type ContactResp = paths['/api/contact']['post']['responses'][201]['content']['application/json'];

export function postContact(
  body: ContactBody,
  opts: { idempotencyKey?: string; captchaToken?: string | null; signal?: AbortSignal } = {},
): Promise<ContactResp> {
  return api<ContactResp>('/api/contact', {
    method: 'POST',
    body,
    idempotencyKey: opts.idempotencyKey,
    // reCAPTCHA rides a header (WAF-safe, keeps the JSON body untouched). A
    // null token (feature off, blocked script, Google down) sends no header;
    // the server fails open behind its rate limits.
    ...(opts.captchaToken ? { headers: { 'X-Recaptcha-Token': opts.captchaToken } } : {}),
    signal: opts.signal,
  });
}

// postFieldLead captures a sales lead as the signed-in rep (RAD §8). The server
// sets source=field and capturedBy from the token; the client sends only the
// prospect's details. The Idempotency-Key makes a retried capture — a double-tap
// or the offline outbox replaying on reconnect (#29) — resolve to one lead. The
// request/response types are pinned to the generated paths map, so a contract
// change surfaces here as a type error.
type FieldLeadBody = paths['/api/leads']['post']['requestBody']['content']['application/json'];
type FieldLeadResp = paths['/api/leads']['post']['responses'][201]['content']['application/json'];

export function postFieldLead(
  body: FieldLeadBody,
  opts: { idempotencyKey?: string; signal?: AbortSignal } = {},
): Promise<FieldLeadResp> {
  return api<FieldLeadResp>('/api/leads', {
    method: 'POST',
    body,
    idempotencyKey: opts.idempotencyKey,
    signal: opts.signal,
  });
}

// queryLeads lists leads for the signed-in caller (RAD §8, §16), newest first,
// paged with an opaque base64url keyset cursor. It backs the field rep's "My
// leads" screen: the data scope is decided server-side from the session — a rep
// only ever sees the leads they captured — so the body carries filters and the
// cursor but never `capturedBy`. It's a POST-for-complex-read (WAF Rule 1): the
// cursor rides the JSON body, never the URL. An empty body asks for the first,
// unfiltered page. Types are pinned to the generated paths map, so a contract
// change surfaces here as a type error.
// requestBody is optional in the contract (an empty body is a valid first-page
// request), so unwrap the optional before indexing into its content.
type LeadsQueryBody = NonNullable<
  paths['/api/leads/query']['post']['requestBody']
>['content']['application/json'];
type LeadsQueryResp = paths['/api/leads/query']['post']['responses'][200]['content']['application/json'];

export function queryLeads(body: LeadsQueryBody = {}, signal?: AbortSignal): Promise<LeadsQueryResp> {
  return api<LeadsQueryResp>('/api/leads/query', { method: 'POST', body, signal });
}

// leadOwners reads the distinct assignees across the caller's visible leads
// (#186) — the stable vocabulary behind the inbox's assignee filter, resolved
// to display identities server-side (#182). Parameterless by contract (an
// empty JSON body); the server returns an empty set to own-/no-scope callers,
// so the dropdown simply stays owner-less for them. Types are pinned to the
// generated paths map.
export type LeadOwner = Schemas['LeadOwner'];
type LeadOwnersResp = paths['/api/leads/owners']['post']['responses'][200]['content']['application/json'];

export function leadOwners(signal?: AbortSignal): Promise<LeadOwnersResp> {
  return api<LeadOwnersResp>('/api/leads/owners', { method: 'POST', body: {}, signal });
}

// leadDetail reads one lead's full record + activity timeline (RAD §9). It's a
// POST-for-complex-read (WAF Rule 1): the lead's document key rides the JSON
// body, never the URL. The server enforces the caller's scope + ownership —
// an own-scope rep gets 404 for a lead they don't own (BOLA). `id` is the exact
// LeadSummary.id (lead::<uuid>). Types are pinned to the generated paths map.
type LeadDetailBody = paths['/api/leads/detail']['post']['requestBody']['content']['application/json'];
type LeadDetailResp = paths['/api/leads/detail']['post']['responses'][200]['content']['application/json'];

export function leadDetail(id: string, signal?: AbortSignal): Promise<LeadDetailResp> {
  const body: LeadDetailBody = { id };
  return api<LeadDetailResp>('/api/leads/detail', { method: 'POST', body, signal });
}

// addLeadNote appends a free-text note to a lead's timeline (RAD §9) and returns
// the created entry so the caller renders it without a re-read. The note text
// rides the JSON body (WAF Rules 1 & 2), never the URL. The server gates it on
// leads×edit and enforces ownership. Types are pinned to the generated paths map.
type LeadNoteBody = paths['/api/leads/notes']['post']['requestBody']['content']['application/json'];
type LeadNoteResp = paths['/api/leads/notes']['post']['responses'][201]['content']['application/json'];

export function addLeadNote(id: string, note: string, signal?: AbortSignal): Promise<LeadNoteResp> {
  const body: LeadNoteBody = { id, note };
  return api<LeadNoteResp>('/api/leads/notes', { method: 'POST', body, signal });
}

// changeLeadStatus moves a lead to a new pipeline stage (RAD §9, #34) and returns
// the created status_changed entry so the caller renders it without a re-read.
// The lead key + fixed-vocabulary stage names ride the JSON body (WAF Rule 1),
// never the URL. `from` is an optional optimistic-concurrency guard — the server
// rejects the move (409 STATUS_CONFLICT) unless it matches the lead's current
// stage. The server gates it on leads×edit and enforces ownership. Types are
// pinned to the generated paths map.
type LeadStatusBody = paths['/api/leads/status']['post']['requestBody']['content']['application/json'];
type LeadStatusResp = paths['/api/leads/status']['post']['responses'][200]['content']['application/json'];

export function changeLeadStatus(
  id: string,
  to: LeadStatusBody['to'],
  from?: LeadStatusBody['from'],
  signal?: AbortSignal,
): Promise<LeadStatusResp> {
  const body: LeadStatusBody = from ? { id, to, from } : { id, to };
  return api<LeadStatusResp>('/api/leads/status', { method: 'POST', body, signal });
}

// assignLead assigns or reassigns a lead to a rep (RAD §9, #35) and returns the
// created `assigned` entry + the resolved owner so the caller renders both
// without a re-read. The lead key + the assignee's email ride the JSON body (WAF
// Rules 1 & 2), never the URL. `from` is an optional optimistic-concurrency guard
// — the server rejects the assignment (409 ASSIGN_CONFLICT) unless it matches the
// lead's current owner. The server gates it on leads×approve (manager/admin,
// desktop-only) and resolves + validates the assignee. Types are pinned to the
// generated paths map.
type LeadAssignBody = paths['/api/leads/assign']['post']['requestBody']['content']['application/json'];
type LeadAssignResp = paths['/api/leads/assign']['post']['responses'][200]['content']['application/json'];

export function assignLead(
  id: string,
  assigneeEmail: string,
  from?: LeadAssignBody['from'],
  signal?: AbortSignal,
): Promise<LeadAssignResp> {
  const body: LeadAssignBody = from !== undefined ? { id, assigneeEmail, from } : { id, assigneeEmail };
  return api<LeadAssignResp>('/api/leads/assign', { method: 'POST', body, signal });
}

// exportLeads downloads the filtered lead set as a CSV attachment (RAD §9, #36).
// It's a POST-for-complex-read (WAF Rule 1): the same fixed-vocabulary
// status/source filters as queryLeads ride the JSON body, never the URL — and no
// cursor/limit, because an export is the whole filtered set. Unlike the JSON
// reads it returns a Blob (+ the server-supplied filename) via apiBlob, so the
// caller can trigger a browser download. The server gates it on the desktop-only
// leads×export key gate and self-audits the export. The request body type is
// pinned to the generated paths map, so a contract change surfaces here.
type LeadsExportBody = NonNullable<
  paths['/api/leads/export']['post']['requestBody']
>['content']['application/json'];

export function exportLeads(body: LeadsExportBody = {}, signal?: AbortSignal): Promise<BlobResponse> {
  return apiBlob('/api/leads/export', { method: 'POST', body, accept: 'text/csv', signal });
}

// postAdminCreateUser onboards a new user with an assigned RBAC role. The server
// gates it to Admin + desktop and audits it (#78); the types are pinned to the
// generated paths map so a contract change surfaces here as a type error.
export type AdminCreateUserBody =
  paths['/api/admin/users']['post']['requestBody']['content']['application/json'];
export type AdminRole = AdminCreateUserBody['role'];
type AdminCreateUserResp = paths['/api/admin/users']['post']['responses'][201]['content']['application/json'];

export function postAdminCreateUser(
  body: AdminCreateUserBody,
  signal?: AbortSignal,
): Promise<AdminCreateUserResp> {
  return api<AdminCreateUserResp>('/api/admin/users', { method: 'POST', body, signal });
}

// queryAdminUsers reads a page of the admin user directory (#183) — every
// account with its role, deactivation state, created date, and last sign-in.
// POST-for-complex-read (WAF Rule 1): the opaque base64url cursor rides the
// JSON body, never the URL. The server gates it to Admin + desktop.
type AdminUsersQueryBody = NonNullable<
  paths['/api/admin/users/query']['post']['requestBody']
>['content']['application/json'];
export type AdminUsersQueryResp =
  paths['/api/admin/users/query']['post']['responses'][200]['content']['application/json'];
export type AdminUserSummary = AdminUsersQueryResp['users'][number];

export function queryAdminUsers(
  body: AdminUsersQueryBody = {},
  signal?: AbortSignal,
): Promise<AdminUsersQueryResp> {
  return api<AdminUsersQueryResp>('/api/admin/users/query', { method: 'POST', body, signal });
}

// postAdminSetUserRole moves an existing account to a different RBAC role. The
// server gates it to Admin + desktop, refuses self-changes and demoting the
// last active admin, and audits it (#78/#183). userId is an opaque UUID path
// segment (WAF-safe); the role travels in the JSON body.
type AdminSetRoleBody =
  paths['/api/admin/users/{userId}/role']['post']['requestBody']['content']['application/json'];
type AdminSetRoleResp =
  paths['/api/admin/users/{userId}/role']['post']['responses'][200]['content']['application/json'];

export function postAdminSetUserRole(
  userId: string,
  role: AdminSetRoleBody['role'],
  signal?: AbortSignal,
): Promise<AdminSetRoleResp> {
  const body: AdminSetRoleBody = { role };
  return api<AdminSetRoleResp>(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
    method: 'POST',
    body,
    signal,
  });
}

// postAdminSetUserStatus deactivates (disabled: true) or reactivates an
// account. Deactivation is an immediate full lockout server-side (sessions
// revoked + every protected route refuses the account). The server gates it to
// Admin + desktop, refuses self-changes and deactivating the last active
// admin, and audits it (#183).
type AdminSetStatusBody =
  paths['/api/admin/users/{userId}/status']['post']['requestBody']['content']['application/json'];
type AdminSetStatusResp =
  paths['/api/admin/users/{userId}/status']['post']['responses'][200]['content']['application/json'];

export function postAdminSetUserStatus(
  userId: string,
  disabled: boolean,
  signal?: AbortSignal,
): Promise<AdminSetStatusResp> {
  const body: AdminSetStatusBody = { disabled };
  return api<AdminSetStatusResp>(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
    method: 'POST',
    body,
    signal,
  });
}

// --- App-level health state, surfaced by the status pill (components/HealthPill). ---

export type HealthState =
  | { kind: 'unknown' }
  | { kind: 'ok'; health: HealthStatus }
  | { kind: 'degraded'; health: HealthStatus }
  | { kind: 'error' };

export const HEALTH_EVENT = 'app:health';
const FRESH_MS = 30_000;

let last: HealthState = { kind: 'unknown' };
let lastAt = 0;
let inflight: Promise<HealthState> | null = null;

export function getLastHealth(): HealthState {
  return last;
}

function publish(next: HealthState): HealthState {
  last = next;
  document.documentElement.dataset.health = next.kind;
  window.dispatchEvent(new CustomEvent<HealthState>(HEALTH_EVENT, { detail: next }));
  return next;
}

// Pings /healthz and broadcasts the result. Coalesces concurrent callers (one
// in-flight request) and serves a cached result within FRESH_MS so per-route
// remounts of the pill don't spam the endpoint. Pass force to bypass the cache.
export function checkHealth(force = false): Promise<HealthState> {
  if (inflight) return inflight;
  if (!force && last.kind !== 'unknown' && Date.now() - lastAt < FRESH_MS) {
    return Promise.resolve(last);
  }
  inflight = getHealthz()
    .then((health) =>
      publish(health.status === 'ok' ? { kind: 'ok', health } : { kind: 'degraded', health }),
    )
    .catch(() => publish({ kind: 'error' }))
    .finally(() => {
      lastAt = Date.now();
      inflight = null;
    });
  return inflight;
}
